import { observable } from "@legendapp/state";
import { getViewabilityReport } from "@/apis/clients/reportingClient";
import {
  createRequestGuard,
  reportScopeKey,
  toErrorMessage,
} from "@/stores/storeHelpers";
import type { PerformanceReportRow, ReportScope } from "@/types/reporting";

interface ViewabilityState {
  rows: PerformanceReportRow[];
  isLoading: boolean;
  error: string | null;
  loadedForScopeKey: string | null;
}

const DEFAULT_VALUES: ViewabilityState = {
  rows: [],
  isLoading: false,
  error: null,
  loadedForScopeKey: null,
};

export const ViewabilityStore$ = observable(DEFAULT_VALUES);

const getScopeKey = (scope: ReportScope) =>
  `viewability:${reportScopeKey(scope)}`;

const guard = createRequestGuard();

export const fetchViewabilityReport = async (scope: ReportScope) => {
  const scopeKey = getScopeKey(scope);
  const result = guard.check(
    scopeKey,
    ViewabilityStore$.loadedForScopeKey.get()
  );
  if (result.outcome === "cached") {
    ViewabilityStore$.assign({ error: null, isLoading: false });
    return;
  }
  if (result.outcome === "in-flight") return;
  const { requestId } = result;
  ViewabilityStore$.assign({ isLoading: true, error: null });

  try {
    const response = await getViewabilityReport(scope);
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    ViewabilityStore$.assign({
      rows: response.results,
      loadedForScopeKey: scopeKey,
    });
  } catch (error) {
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    ViewabilityStore$.error.set(toErrorMessage(error));
  } finally {
    if (!guard.isStale(requestId)) {
      ViewabilityStore$.isLoading.set(false);
    }
  }
};

export const clearViewabilityReport = () => {
  guard.invalidate();
  ViewabilityStore$.assign({
    rows: [],
    isLoading: false,
    loadedForScopeKey: null,
    error: null,
  });
};
